import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Settings } from '../flows/settings';
import { MsalHelperService } from '../services/msal-helper.service';
import { FlowManagementService } from '../services/flow-management.service';
import { AccountInfo } from '@azure/msal-browser';
import { map, Observable, Subscription } from 'rxjs';
import { IWorkspace, PaymentService } from '../services/payment.service';
import { DomSanitizer } from '@angular/platform-browser';
import { faAddressCard } from '@fortawesome/free-solid-svg-icons';
import { StorageRouterService } from '../services/storage-router.service';

@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class SettingsComponent implements OnInit, OnDestroy {

  constructor(
    private storageDexieService: StorageRouterService,
    private flowManagementService: FlowManagementService,
    private msalHelperService: MsalHelperService,
    public paymentService: PaymentService,
    private sanitizer: DomSanitizer
    ) {

      this.user$ = this.msalHelperService.user$;
      this.membership$ = this.paymentService.membership$;
      this.licenseHasProOrTeams$ = this.paymentService.membership$.pipe(
        map(m => ['Trial', 'Active', 'Enterprise'].includes(m?.status))
      );
      this.users$ = this.paymentService.subscription$.pipe(map((subscription)=>{
        return subscription?.users;
      }));
      this.workspace$ = this.paymentService.subscription$.pipe(map((subscription)=>{
        const [first] = subscription?.workspace || [];
        return first;
      }))

  }

  // public server = true;
  // public api = false;
  public optionScanAll = false;
  public optionApi = true;
  public optionFlowBatchingApi = true;

  public optionDefaultRoute = '';
  public optionAutoRefreshHours = 1;

  public allColumns = [
    { key: 'shareOwners', label: 'Owner', defaultOn: true },
    { key: 'displayName', label: 'Display Name', defaultOn: true },
    { key: 'monitor', label: 'Monitor', defaultOn: true },
    { key: 'runsColorBar', label: 'Runs (status bar)', defaultOn: true },
    { key: 'tags', label: 'Tags', defaultOn: true },
    { key: 'trigger', label: 'Trigger', defaultOn: true },
    { key: 'triggerKind', label: 'Kind', defaultOn: true },
    { key: 'actionsCount', label: '# Actions', defaultOn: false, help: 'Due to changes in Flow API, FlowStudio no longer reliably receives all actions in a flow summary. This column no longer gives useful data.' },
    { key: 'flowState', label: 'On/Off', defaultOn: true },
    { key: 'modified', label: 'Last Modified', defaultOn: true },
    { key: 'runsSort', label: 'Runs (count)', defaultOn: true },
    { key: 'runsFailCount', label: 'Fails', defaultOn: true },
    { key: 'lastRun', label: 'Last Ran', defaultOn: true },
  ];
  public selectedColumns: string[] = this.allColumns.filter(c => c.key !== 'actionsCount').map(c => c.key);

  public allDeprecatedTools = [
    { key: 'editInDesigner', label: 'Edit in Designer (context menu)', defaultOn: false, help: 'Loads the Flow portal in a popup, but it opens the older Flow designer. Retired.' },
    { key: 'newSubmenu', label: 'New … submenu (context menu)', defaultOn: false },
    { key: 'powerApps', label: 'Power Apps (sidebar)', defaultOn: true, help: 'Shows the older Power Apps sidebar tool.' },
    { key: 'approvals', label: 'Approvals (sidebar)', defaultOn: true, help: 'To read approvals, it is easier and more reliable to read directly from Dataverse.' },
    { key: 'httpRequest', label: 'HTTP Request (sidebar)', defaultOn: true, help: 'This helped with Flow trigger URL migration, but Microsoft has disabled and hidden the old trigger URLs, so this tool is less useful now.' },
  ];
  public visibleDeprecatedTools: string[] = ['powerApps', 'approvals', 'httpRequest'];
  public allFlowLinks = [
    { key: 'v2', label: 'Maker Portal v2 hover link', defaultOn: true },
    { key: 'v3', label: 'Maker Portal v3 hover link', defaultOn: true },
    { key: 'v4', label: 'Copilot Studio v4 hover link', defaultOn: true, help: 'Only appears for solution flows with a workflowEntityId.' },
  ];
  public visibleFlowLinks: string[] = this.allFlowLinks.map(link => link.key);
  public allRunScanGroups = [
    { key: 'monitored', label: 'Monitored flows', help: 'Flows marked for Teams monitoring. These are scanned first so monitored status and alerts stay current.' },
    { key: 'cached-runs', label: 'Flows with cached runs', help: 'Flows that already have run history in local cache. Refreshing these keeps existing run timelines up to date.' },
    { key: 'started-recently-modified', label: 'Recently modified active flows', help: 'Started flows modified in the recent scan window. These are more likely to have new run or trigger behavior.' },
    { key: 'started-heavy-actions', label: 'Large active flows', help: 'Started flows with many actions. These can be important operational flows, but may take longer to scan.' },
    { key: 'rest', label: 'All other active flows', help: 'The remaining started flows not matched by the earlier priority groups. Turn this off to scan only priority flows.' },
  ];
  public selectedRunScanGroups: string[] = this.allRunScanGroups.map(g => g.key);

  public defaultRouteOptions = [
    { value: '', text: '(default)' },
    { value: '/flows', text: 'Flows' },
    { value: '/admin', text: 'Admin Flows' },
    { value: '/approvals', text: 'Approvals' },
    { value: '/connections', text: 'Connections' },
    { value: '/search', text: 'Search' },
    { value: '/migrate', text: 'Migrate Flows' },
    { value: '/diff', text: 'Compare Flows' },
    { value: '/powerapps', text: 'Power Apps' },
    { value: '/teams', text: 'Teams Dashboard' },
    { value: '/clipboard', text: 'Clipboard' },
  ];

  public licenseHasProOrTeams$: Observable<boolean>;

  // VSCode-style table of contents for the settings page. Selecting an item
  // shows only that panel (`selectSection`). General is the base settings tile;
  // its children (Grid / Refresh / Tools) are sub-panels under it.
  public sections = [
    {
      id: 'general',
      label: 'General',
      children: [
        { id: 'flows-grid', label: 'Grid' },
        { id: 'run-history', label: 'Refresh' },
        { id: 'tools', label: 'Tools' },
      ],
    },
    { id: 'workspace', label: 'Workspace' },
    { id: 'account', label: 'Account' },
  ];
  public activeSection = 'general';

  public user$: Observable<AccountInfo>;
  public users$: Observable<any[]>;
  public workspace$: Observable<IWorkspace>;
  public membership$: Observable<any>;
  public photo;
  private photoObjectUrl: string;
  public subscription = new Subscription();
  public icons = {
    faAddressCard
  }

  ngOnInit() {
    this.storageDexieService.getConfig('optionScanAll').then(value=>{
      this.optionScanAll = value.value;
    }).catch(()=>{
      // defaults to favourites
      this.optionScanAll = false;
    });
    // this.storageDexieService.getSetting(Settings.SETTING_SERVER).then(setting => {
    //   this.server = (setting.value != false);
    // });
    // this.storageDexieService.getSetting(Settings.SETTING_USEAPI).then(setting => {
    //   this.api = (setting.value == true);
    // });
    this.subscription.add(this.user$.subscribe(user=>{
      this.flowManagementService.getUserPhoto(user?.['oid']).then(photo=>{
        this.setPhoto(photo);
      }).catch(() => {
        this.setPhoto(null);
      });

    }));

    this.storageDexieService.getConfig('optionApi').then(value=>{
      this.optionApi = (value.value !== false);
    }).catch(()=>{
      this.optionApi = true;
    });

    this.storageDexieService.getConfig('optionFlowBatchingApi').then(value=>{
      this.optionFlowBatchingApi = (value.value !== false);
    }).catch(()=>{
      this.optionFlowBatchingApi = true;
    });

    this.storageDexieService.getConfig(Settings.SETTING_LASTROUTE).then(v => {
      this.optionDefaultRoute = v?.value || '';
    }).catch(() => {});

    this.storageDexieService.getConfig(Settings.SETTING_AUTO_REFRESH_HOURS).then(v => {
      this.optionAutoRefreshHours = this.normalizeAutoRefreshHours(v?.value);
    }).catch(() => {
      this.optionAutoRefreshHours = 1;
    });

    this.storageDexieService.getConfig(Settings.SETTING_GRID_COLUMNS_VISIBLE).then(v => {
      if (Array.isArray(v?.value)) { this.selectedColumns = v.value; }
    }).catch(() => {});

    this.storageDexieService.getConfig(Settings.SETTING_DEPRECATED_TOOLS_VISIBLE).then(v => {
      if (Array.isArray(v?.value)) { this.visibleDeprecatedTools = v.value; }
    }).catch(() => {});

    this.storageDexieService.getConfig(Settings.SETTING_FLOW_LINKS_VISIBLE).then(v => {
      if (Array.isArray(v?.value)) { this.visibleFlowLinks = v.value; }
    }).catch(() => {});

    this.storageDexieService.getConfig(Settings.SETTING_RUN_SCAN_GROUPS).then(v => {
      if (Array.isArray(v?.value)) { this.selectedRunScanGroups = this.normalizeRunScanGroups(v.value); }
    }).catch(() => {});

    this.configHandler();
  }

  selectSection(id: string): void {
    this.activeSection = id;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.revokePhotoObjectUrl();
  }

  private setPhoto(photo: Blob | null) {
    this.revokePhotoObjectUrl();
    this.photo = null;

    if (!(photo instanceof Blob) || photo.size === 0) {
      return;
    }

    this.photoObjectUrl = URL.createObjectURL(photo);
    this.photo = this.sanitizer.bypassSecurityTrustUrl(this.photoObjectUrl);
  }

  private revokePhotoObjectUrl() {
    if (this.photoObjectUrl) {
      URL.revokeObjectURL(this.photoObjectUrl);
      this.photoObjectUrl = null;
    }
  }

  private configHandler(): any {
    this.paymentService.verify(true);
  }

  valueChange(setting) {
    // debugger;
    // console.log(arguments);
    // console.log(this.server);

    switch (setting) {
      case 'optionScanAll':
        this.storageDexieService.putConfig("optionScanAll", {
          key: 'optionScanAll',
          value: this.optionScanAll
        });
        break;
      case "optionApi":
        this.storageDexieService.putConfig("optionApi", {
          key: 'optionApi',
          value: this.optionApi
        });
        break;
      case "optionFlowBatchingApi":
        // Route through FlowManagementService.setBatchingEnabled so it
        // persists AND broadcasts via the BehaviorSubject — keeps all batch
        // call sites in sync without a page reload.
        this.flowManagementService.setBatchingEnabled(this.optionFlowBatchingApi);
        break;
      case "optionDefaultRoute":
        this.storageDexieService.putConfig(Settings.SETTING_LASTROUTE, { key: Settings.SETTING_LASTROUTE, value: this.optionDefaultRoute });
        break;
      case "optionAutoRefreshHours":
        this.optionAutoRefreshHours = this.normalizeAutoRefreshHours(this.optionAutoRefreshHours);
        this.storageDexieService.putConfig(Settings.SETTING_AUTO_REFRESH_HOURS, { key: Settings.SETTING_AUTO_REFRESH_HOURS, value: this.optionAutoRefreshHours });
        break;
      case "selectedColumns":
        this.storageDexieService.putConfig(Settings.SETTING_GRID_COLUMNS_VISIBLE, { key: Settings.SETTING_GRID_COLUMNS_VISIBLE, value: this.selectedColumns });
        break;
      case "visibleDeprecatedTools":
        this.storageDexieService.putConfig(Settings.SETTING_DEPRECATED_TOOLS_VISIBLE, { key: Settings.SETTING_DEPRECATED_TOOLS_VISIBLE, value: this.visibleDeprecatedTools });
        this.storageDexieService.deprecatedToolsVisible$.next(this.visibleDeprecatedTools);
        break;
      case "visibleFlowLinks":
        this.storageDexieService.putConfig(Settings.SETTING_FLOW_LINKS_VISIBLE, { key: Settings.SETTING_FLOW_LINKS_VISIBLE, value: this.visibleFlowLinks });
        break;
      case "selectedRunScanGroups":
        this.selectedRunScanGroups = this.normalizeRunScanGroups(this.selectedRunScanGroups);
        this.storageDexieService.putConfig(Settings.SETTING_RUN_SCAN_GROUPS, { key: Settings.SETTING_RUN_SCAN_GROUPS, value: this.selectedRunScanGroups });
        break;


      // case "server":
      // default:
      //   this.storageDexieService.updateSettings({
      //     '_id': Settings.SETTING_SERVER,
      //     value: this.server
      //   });
      //   break;
    }
  }

  private normalizeAutoRefreshHours(value: any): number {
    const hours = Number(value);
    if (!Number.isFinite(hours)) {
      return 1;
    }
    return Math.max(1, Math.round(hours * 100) / 100);
  }

  toggleColumn(key: string) {
    if (this.selectedColumns.includes(key)) {
      this.selectedColumns = this.selectedColumns.filter(k => k !== key);
    } else {
      this.selectedColumns = [...this.selectedColumns, key];
    }
    this.valueChange('selectedColumns');
  }

  toggleDeprecatedTool(key: string) {
    if (this.visibleDeprecatedTools.includes(key)) {
      this.visibleDeprecatedTools = this.visibleDeprecatedTools.filter(k => k !== key);
    } else {
      this.visibleDeprecatedTools = [...this.visibleDeprecatedTools, key];
    }
    this.valueChange('visibleDeprecatedTools');
  }

  toggleFlowLink(key: string) {
    if (this.visibleFlowLinks.includes(key)) {
      this.visibleFlowLinks = this.visibleFlowLinks.filter(k => k !== key);
    } else {
      this.visibleFlowLinks = [...this.visibleFlowLinks, key];
    }
    this.valueChange('visibleFlowLinks');
  }

  toggleRunScanGroup(key: string) {
    if (this.selectedRunScanGroups.includes(key)) {
      this.selectedRunScanGroups = this.selectedRunScanGroups.filter(k => k !== key);
    } else {
      this.selectedRunScanGroups = [...this.selectedRunScanGroups, key];
    }
    this.valueChange('selectedRunScanGroups');
  }

  private normalizeRunScanGroups(value: any): string[] {
    const allowed = new Set(this.allRunScanGroups.map(g => g.key));
    return (Array.isArray(value) ? value : [])
      .filter((key) => allowed.has(key));
  }

  logout() {
    this.msalHelperService.logout();
  }

  test(){
    this.flowManagementService.testSharePointDataset().then((res)=>{
      console.log(res);
    })
  }

}
